
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  Workorder,
  CostPart,
  Part,
  WorkordersApi,
  PerformoSettings,
  ValidationProblemDetails
} from 'performo-api';

import { ColumnDisplayOption } from '@/models/ColumnDisplayOption';

import AddPurchasedPartComponent from '@/components/orders/AddPurchasedPartComponent.vue';
import AddInventoryPartComponent from '@/components/orders/AddInventoryPartComponent.vue';
import UpdateCostPart from '@/components/orders/UpdateCostPart.vue';
import DeleteCostPart from '@/components/orders/DeleteCostPart.vue';

import { mapGetters } from 'vuex';

@Component({
  components: {
    AddPurchasedPartComponent,
    AddInventoryPartComponent,
    UpdateCostPart,
    DeleteCostPart
  },
  computed: mapGetters({
    settings: 'settings',
    api: 'ordersApi'
  })
})
export default class OrderPartListComponent extends Vue {
  private api!: WorkordersApi;

  @Prop({ required: true })
  private order!: Workorder;

  private settings!: PerformoSettings;

  get costPartsDisplayOptions(): ColumnDisplayOption[] {
    if (this.settings.advancedPartsFunctionalityFlag) {
      return [
        { displayName: 'Part Name', columnName: 'partName', dontSort: true },
        { displayName: 'Part Code', columnName: 'partCode', dontSort: true },
        {
          displayName: 'Warehouse',
          columnName: 'partWarehouse',
          dontSort: true
        },
        {
          displayName: 'Requested',
          columnName: 'partQuantityWanted',
          dontSort: true
        },
        {
          displayName: 'Supplied',
          columnName: 'partQuantity',
          dontSort: true
        },
        { displayName: 'Units', columnName: 'partUnits', dontSort: true },
        { displayName: 'Cost', columnName: 'partUnitCost', dontSort: true },
        { displayName: 'Type', columnName: 'partCostType', dontSort: true },
        {
          displayName: 'Actions',
          columnName: 'partAction',
          dontSort: true,
          width: '75px'
        }
      ];
    } else {
      return [
        { displayName: 'Part Name', columnName: 'partName', dontSort: true },
        { displayName: 'Part Code', columnName: 'partCode', dontSort: true },
        {
          displayName: 'Warehouse',
          columnName: 'partWarehouse',
          dontSort: true
        },
        {
          displayName: 'Quantity',
          columnName: 'partQuantity',
          dontSort: true
        },
        { displayName: 'Units', columnName: 'partUnits', dontSort: true },
        { displayName: 'Cost', columnName: 'partUnitCost', dontSort: true },
        { displayName: 'Type', columnName: 'partCostType', dontSort: true },
        {
          displayName: 'Actions',
          columnName: 'partAction',
          dontSort: true,
          width: '75px'
        }
      ];
    }
  }

  private partUpdatedHandler(part: CostPart) {
    this.$emit('part-updated', part);
  }

  private partDeletedHandler() {
    this.$emit('part-deleted');
  }

  private async purchasedPartAddedHandler(part: CostPart) {
    this.$emit('partAdded', part);
  }

  private async inventoryPartAddedHandler(parts: Part[]) {
    for (const part of parts) {
      try {
        await this.api.addPartToOrder({
          costPartCreateCommand: this.convertToCostPartCreateCommand(part)
        });
        this.$emit('partAdded', part);
      } catch (error) {
        this.errorHandler(error);
        break;
      }
    }
  }

  private async errorHandler(error: ValidationProblemDetails | Response) {
    this.$emit('error', error);
  }

  private convertToCostPartCreateCommand(part: Part) {
    if (part.partId) {
      // default request quantity to 1 if invalid input
      if (part.requestQuantityInput && part.requestQuantityInput <= 0) {
        part.requestQuantityInput = 1;
      }
      return {
        workorderId: this.order.workorderId,
        partIDx: part.partId.toString(),
        partCode: part.partCode,
        partName: part.partName,
        partUnitCost: part.partUnitCost,
        partUnits: part.costUnits,
        partCostType: 'Inventory',
        partTransactionDate: new Date(),
        propertyId: part.propertyId,
        partAssetId: part.partAssetId,
        quantity: part.requestQuantityInput
      };
    }
  }
}
